/**
 * GOLDEN SLICE (ARCH-7): the coach exercise library's single source of truth on the client.
 * Walkthrough of the whole vertical: docs/golden-slice.md. Rules: docs/state-model.md (Rule 3).
 *
 * Conventions this store demonstrates (copy them for new domains):
 * - ONE owner. Components never keep their own copy of the list. They read through the
 *   selector hooks in ./exerciseLibraryStore.selectors.ts, and every mutation goes through
 *   an action here, so a write updates every screen at once. No window events, no refetch-after-write.
 * - Actions own all I/O, via the data module (src/data/exercisesApi.ts). They're non-throwing:
 *   each returns the apiClient result, and the caller decides what to show (toasts, dialogs).
 *   The store never toasts.
 * - Normalized cache: `byId` + `order` (server order, most recently updated first).
 * - Optimistic writes with explicit rollback: delete removes the row immediately and restores it
 *   at the same position on failure; duplicate shows a temp row and swaps in the real one.
 *   `pending` marks rows with a write in flight, so the UI can disable their actions.
 * - Loading is explicit and de-duplicated: `load()` is safe to call from every consumer's mount;
 *   concurrent calls share one request, and a loaded store isn't refetched unless `force`.
 * - Typed end to end (`Exercise`), no `any`.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ApiResponse } from '@/lib/apiClient';
import type { Exercise } from '@/types/exercises';
import * as exercisesApi from '@/data/exercisesApi';
import type { ExerciseResult, SaveExerciseResult } from '@/data/exercisesApi';
import type { SaveExerciseBody } from '@schemas/exercises';

export type ExerciseLibraryStatus = 'idle' | 'loading' | 'ready' | 'error';
export type PendingOp = 'delete' | 'duplicate';

interface ExerciseLibraryState {
  byId: Record<string, Exercise>;
  /** Ids in display order. Temp ids (`tmp_…`) appear here while a duplicate is in flight. */
  order: string[];
  status: ExerciseLibraryStatus;
  error: string | null;
  /** Rows with a write in flight, keyed by exercise id. */
  pending: Record<string, PendingOp>;
  /** Ids whose detail-only columns (body regions, mechanics, …) have been fetched. */
  detailLoaded: Record<string, true>;
}

interface ExerciseLibraryActions {
  load: (opts?: { force?: boolean }) => Promise<void>;
  loadDetail: (id: string) => Promise<ExerciseResult | null>;
  save: (exercise: SaveExerciseBody['exercise']) => Promise<SaveExerciseResult>;
  remove: (id: string, opts?: { hard?: boolean }) => Promise<ApiResponse>;
  duplicate: (id: string) => Promise<ExerciseResult>;
  reset: () => void;
}

export type ExerciseLibraryStore = ExerciseLibraryState & ExerciseLibraryActions;

const initialState: ExerciseLibraryState = {
  byId: {},
  order: [],
  status: 'idle',
  error: null,
  pending: {},
  detailLoaded: {},
};

/** Shared by concurrent `load()` callers; lives outside state because it isn't renderable. */
let inflightLoad: Promise<void> | null = null;

const withoutKey = <T>(record: Record<string, T>, key: string): Record<string, T> => {
  const { [key]: _omit, ...rest } = record;
  return rest;
};

export const useExerciseLibraryStore = create<ExerciseLibraryStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      load: async ({ force = false } = {}) => {
        if (inflightLoad) return inflightLoad;
        if (!force && get().status === 'ready') return;

        set({ status: 'loading', error: null });
        inflightLoad = (async () => {
          const res = await exercisesApi.listExercises();
          if (res.ok && res.exercises) {
            const byId: Record<string, Exercise> = {};
            for (const ex of res.exercises) byId[ex.id] = ex;
            set({ byId, order: res.exercises.map((ex) => ex.id), status: 'ready', detailLoaded: {} });
          } else {
            // Keep whatever we already had on screen; just surface the failure.
            set({ status: 'error', error: res.message || res.error || 'Failed to load exercises' });
          }
        })().finally(() => {
          inflightLoad = null;
        });
        return inflightLoad;
      },

      loadDetail: async (id) => {
        if (get().detailLoaded[id]) return null;
        const res = await exercisesApi.getExercise(id);
        const exercise = res.exercise;
        if (exercise) {
          set((s) => ({
            byId: { ...s.byId, [id]: exercise },
            detailLoaded: { ...s.detailLoaded, [id]: true },
          }));
        }
        return res;
      },

      save: async (input) => {
        const res = await exercisesApi.saveExercise(input);
        const saved = res.exercise;
        if (saved) {
          set((s) => ({
            byId: { ...s.byId, [saved.id]: saved },
            // New rows go first (the list is most-recent-first); edits keep their place.
            order: s.byId[saved.id] ? s.order : [saved.id, ...s.order],
            // The save response is the full row, so the detail columns are current too.
            detailLoaded: { ...s.detailLoaded, [saved.id]: true },
          }));
        }
        return res;
      },

      remove: async (id, opts) => {
        const snapshot = get().byId[id];
        const index = get().order.indexOf(id);

        // Optimistic: drop the row now.
        set((s) => ({
          byId: withoutKey(s.byId, id),
          order: s.order.filter((x) => x !== id),
          pending: { ...s.pending, [id]: 'delete' },
        }));

        const res = await exercisesApi.deleteExercise(id, opts);

        set((s) => {
          const pending = withoutKey(s.pending, id);
          if (res.ok || !snapshot) return { pending, detailLoaded: withoutKey(s.detailLoaded, id) };
          // Rollback: put the row back where it was.
          const order = [...s.order];
          order.splice(index < 0 ? 0 : Math.min(index, order.length), 0, id);
          return { pending, byId: { ...s.byId, [id]: snapshot }, order };
        });
        return res;
      },

      duplicate: async (id) => {
        const source = get().byId[id];
        const tempId = `tmp_${id}_${Date.now()}`;

        // Optimistic: show a placeholder copy at the top while the server works.
        if (source) {
          set((s) => ({
            byId: { ...s.byId, [tempId]: { ...source, id: tempId, name: `${source.name} (Copy…)`, isPublic: false } },
            order: [tempId, ...s.order],
            pending: { ...s.pending, [id]: 'duplicate' },
          }));
        }

        const res = await exercisesApi.duplicateExercise(id);
        const copy = res.exercise;

        set((s) => {
          const byId = withoutKey(s.byId, tempId);
          const pending = withoutKey(s.pending, id);
          if (!copy) return { byId, order: s.order.filter((x) => x !== tempId), pending };
          // Reconcile: the real row takes the placeholder's slot.
          byId[copy.id] = copy;
          const order = s.order.includes(tempId)
            ? s.order.map((x) => (x === tempId ? copy.id : x))
            : [copy.id, ...s.order];
          return { byId, order, pending, detailLoaded: { ...s.detailLoaded, [copy.id]: true } };
        });
        return res;
      },

      reset: () => {
        inflightLoad = null;
        set(initialState);
      },
    }),
    { name: 'ExerciseLibraryStore' },
  ),
);
